import { useState, useEffect, useCallback } from "react";
import { usePropertySharing } from "@/hooks/usePropertySharing";
import { usePropertyContext } from "@/contexts/PropertyContext";
import GlassCard from "./GlassCard";
import { Button } from "@/components/ui/button";
import { Mail, Check, X, Loader2 } from "lucide-react";

const PendingInvites = () => {
    const { checkMyInvites, acceptInvite, rejectInvite } = usePropertySharing(null);
    const { refetch } = usePropertyContext();
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(null);

    useEffect(() => {
        let active = true;
        
        const loadInvites = async () => {
            setLoading(true);
            const data = await checkMyInvites();
            if (active) {
                setInvites(data);
                setLoading(false);
            }
        };

        loadInvites();

        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAccept = async (invite) => {
        setProcessing(invite.id);
        const success = await acceptInvite(invite.id, invite.property_id);
        if (success) {
            setInvites(prev => prev.filter(i => i.id !== invite.id));
            // Refetch properties to update the dashboard
            await refetch();
        }
        setProcessing(null);
    };

    const handleReject = async (invite) => {
        setProcessing(invite.id);
        const success = await rejectInvite(invite.id);
        if (success) {
            setInvites(prev => prev.filter(i => i.id !== invite.id));
        }
        setProcessing(null);
    };

    if (loading || invites.length === 0) return null;

    return (
        <div className="space-y-3 mb-6">
            <div className="flex items-center gap-2 px-1">
                <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Property Invites</h3>
            </div>

            {invites.map((invite) => (
                <GlassCard key={invite.id} className="p-4 border-amber-500/30 bg-amber-500/5">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-full bg-amber-100 text-amber-600">
                                <Mail className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="font-medium text-foreground">
                                    You've been invited to join <span className="font-bold">{invite.properties?.name}</span>
                                </p>
                                <p className="text-sm text-muted-foreground">
                                    {invite.properties?.suburb || "No location specified"}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                className="h-9 w-9 p-0 rounded-full border-destructive/30 text-destructive hover:bg-destructive/10"
                                onClick={() => handleReject(invite)}
                                disabled={processing === invite.id}
                            >
                                <X className="w-4 h-4" />
                            </Button>
                            <Button
                                size="sm"
                                className="h-9 px-4 rounded-full bg-primary hover:bg-primary/90"
                                onClick={() => handleAccept(invite)}
                                disabled={processing === invite.id}
                            >
                                {processing === invite.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <>
                                        <Check className="w-4 h-4 mr-1.5" />
                                        Accept
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </GlassCard>
            ))}
        </div>
    );
};

export default PendingInvites;
